import { api, ApiError } from '../lib/api.js';
import { el, render, fmtDateOnly, sortableHeader } from '../lib/dom.js';
import { canWrite } from '../lib/session.js';
import { pageHeader, badge, avatar, filterSelect, actionMenu, renderPagination, skeletonRows, emptyState, errorState, fullRow } from '../lib/ui.js';

// ---------------------------------------------------------------------------
// Expiring trials. Server-side filter (within N days), sort, and paging —
// same reasoning as orgs.js: the org table is shared with a live product.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const COLS = 7;

function urgencyBadge(daysLeft) {
  if (daysLeft === null) return null;
  if (daysLeft <= 0) return badge('Expired', 'red', { dot: true });
  if (daysLeft <= 2) return badge('Critical', 'red', { dot: true });
  if (daysLeft <= 5) return badge('Soon', 'amber', { dot: true });
  return badge('Upcoming', 'blue', { dot: true });
}

export function trialsView({ navigate }) {
  const state = { withinDays: 30, sortBy: 'trialEndsAt', sortDir: 'asc', page: 1, total: 0 };
  const tbody = el('tbody', {});
  const footer = el('div', {});
  const theadRow = el('tr', {});

  const reload = () => {
    state.page = 1;
    void load();
  };

  function row(org) {
    const open = (tab) => navigate(`/orgs/${org.id}${tab ? `?tab=${tab}` : ''}`);
    return el(
      'tr',
      { class: 'is-clickable', onclick: (e) => !e.target.closest('button') && open(canWrite() ? 'settings' : undefined) },
      el(
        'td',
        {},
        el(
          'div',
          { class: 'cell-main' },
          avatar(org.name, { square: true }),
          el('div', { class: 'cell-main__text' }, el('span', { class: 'cell-title' }, org.name), !org.isActive ? el('span', { class: 'cell-sub' }, 'Inactive') : null),
        ),
      ),
      el('td', {}, org.industry || '—'),
      el('td', {}, org.country || '—'),
      el('td', { class: 'nowrap' }, fmtDateOnly(org.trialEndsAt, org.timeZone || 'UTC')),
      el('td', { class: 'col-center' }, org.daysLeft === null ? '—' : String(Math.max(0, org.daysLeft))),
      el('td', {}, urgencyBadge(org.daysLeft)),
      el(
        'td',
        { class: 'col-actions' },
        actionMenu(
          [
            { label: 'View organisation', iconName: 'menu-view', onSelect: () => open() },
            canWrite() && { label: 'Extend trial / lift restrictions', iconName: 'menu-edit', onSelect: () => open('settings') },
          ],
          { label: `Actions for ${org.name}` },
        ),
      ),
    );
  }

  async function load() {
    render(
      theadRow,
      sortableHeader(state, 'name', 'Organisation', reload),
      el('th', {}, 'Industry'),
      el('th', {}, 'Country'),
      sortableHeader(state, 'trialEndsAt', 'Trial ends', reload),
      el('th', { class: 'col-center' }, 'Days left'),
      el('th', {}, 'Urgency'),
      el('th', { class: 'col-actions' }, ''),
    );
    render(tbody, skeletonRows(COLS, 5));
    try {
      const data = await api.listExpiringTrials({ withinDays: state.withinDays, sortBy: state.sortBy, sortDir: state.sortDir, page: state.page, pageSize: PAGE_SIZE });
      state.total = data.total;
      render(
        tbody,
        data.organisations.length === 0
          ? fullRow(COLS, emptyState({ title: 'No trials ending', text: `No organisation has a trial ending within ${state.withinDays} days.`, compact: true }))
          : data.organisations.map(row),
      );
      renderPagination(footer, {
        page: state.page,
        pageSize: PAGE_SIZE,
        total: state.total,
        noun: 'organisations',
        onPage: (n) => {
          state.page = n;
          void load();
        },
      });
    } catch (err) {
      render(tbody, fullRow(COLS, errorState({ title: 'Failed to load expiring trials', text: err instanceof ApiError ? err.message : 'Check the API and try again.', onRetry: () => void load() })));
      render(footer);
    }
  }

  void load();

  return el(
    'div',
    { class: 'stack' },
    pageHeader({
      title: 'Expiring trials',
      subtitle: 'Organisations whose trial window ends soon. Open one to extend its trial dates or switch it to CMAI Core before members lose access.',
    }),
    el(
      'div',
      { class: 'toolbar' },
      filterSelect({
        label: 'Ending within',
        value: String(state.withinDays),
        options: [
          { value: '7', label: '7 days' },
          { value: '14', label: '14 days' },
          { value: '30', label: '30 days' },
          { value: '60', label: '60 days' },
          { value: '90', label: '90 days' },
          { value: '365', label: '12 months' },
        ],
        onChange: (v) => {
          state.withinDays = Number(v);
          reload();
        },
      }),
    ),
    el('div', { class: 'table-card' }, el('div', { class: 'table-scroll' }, el('table', { class: 'table' }, el('thead', {}, theadRow), tbody)), footer),
  );
}
